// ============================================================
// Cloudflare Turnstile — die zwei Fallen, beide schon einmal zugeschlagen.
//
// 1. Der Behälter heißt `pruefung`, NIE `turnstile`. Der Browser legt für
//    jedes Element mit id eine gleichnamige Eigenschaft auf `window` an;
//    `window.turnstile` wäre dann das <div>, die api.js hielte sich für
//    schon geladen, zeichnete nichts, und der Knopf bliebe für immer
//    gesperrt. (Live nachgewiesen auf webgis, 08.09.)
//
// 2. `render=explicit` und die deklarativen `data-…`-Attribute schließen
//    einander aus. Mit explicit zeichnet Cloudflare `.cf-turnstile` nicht
//    mehr von selbst — ruft niemand render(), passiert nichts, dieselbe
//    Sackgasse. Wir BRAUCHEN explicit, weil der Sitekey erst aus
//    GET /auth/config kommt; der Aufruf unten ist also Pflicht, nicht Kür.
//
// Dazu: Wertmarken sind EINMAL gültig. Nach jedem fehlgeschlagenen Absenden
// muss reset() laufen, sonst scheitert der zweite Versuch serverseitig an
// `invalid-input-response`.
// ============================================================

export const BEHAELTER_ID = "pruefung";
const SKRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=turnstileBereit";

export interface TurnstileOptionen {
  sitekey: string;
  action?: string;
  theme?: "auto" | "light" | "dark";
  /** `flexible`: so breit wie der Behälter (Canvas: volle Breite statt fester 300 px). */
  size?: "normal" | "flexible" | "compact";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
}

export interface TurnstileApi {
  render(behaelter: string | HTMLElement, o: TurnstileOptionen): string | undefined;
  reset(id?: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    turnstileBereit?: () => void;
  }
}

let ladevorgang: Promise<TurnstileApi> | null = null;

/**
 * Lädt api.js genau einmal und löst auf, sobald Cloudflare `turnstileBereit`
 * gerufen hat. Mehrfache Aufrufe (zwei Bildschirme, StrictMode) teilen sich
 * dasselbe Versprechen — ein zweites <script> derselben Adresse würde die
 * schon gezeichneten Widgets aus dem Tritt bringen.
 */
export function ladeTurnstile(): Promise<TurnstileApi> {
  if (ladevorgang) return ladevorgang;
  ladevorgang = new Promise<TurnstileApi>((erfuellen, ablehnen) => {
    // Schon da (heißes Neuladen im Dev, zweiter Besuch der Anmeldung).
    if (window.turnstile && typeof window.turnstile.render === "function") {
      erfuellen(window.turnstile);
      return;
    }
    window.turnstileBereit = () => {
      if (window.turnstile && typeof window.turnstile.render === "function") erfuellen(window.turnstile);
      else ablehnen(new Error("Turnstile meldet sich, bringt aber keine render-Funktion mit."));
    };
    const s = document.createElement("script");
    s.src = SKRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onerror = () => {
      ladevorgang = null; // ein späterer Versuch darf es noch einmal probieren
      ablehnen(new Error("Die Prüfung von Cloudflare ließ sich nicht laden."));
    };
    document.head.appendChild(s);
  });
  return ladevorgang;
}
